import React, { useMemo, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { monthDisplayFormat } from '~libs/dayjs';
import { useToast } from '~libs/toast';

import Modal from '~components/Modal';
import { Form as WizardForm } from '~components/wizard-form';
import DestructiveConfirmDialog from '~support-at-home/components/DestructiveConfirmDialog';

import {
  buildRollbackSteps,
  canRollBack,
  ROLLBACK_ACKNOWLEDGEMENTS,
  RollbackCheckValue,
} from '~support-at-home/forms/statements/rollback';

import { RollbackInvite, RollbackRequest, useRollbackMutation } from '~support-at-home/actions/statements';

interface ComponentProps {
  token: string;
  invite: RollbackInvite;
  isOpen: boolean;
  setIsOpen: (toggle: boolean) => void;
  onRolledBack?: () => void;
}

const plural = (count: number) => `statement${count === 1 ? '' : 's'}`;

// TAV-3491: opened only from a TurnPoint Support link. The month and clients come from the link.
const StatementsRollbackWizard: React.FC<ComponentProps> = ({ token, invite, isOpen, setIsOpen, onRolledBack }) => {
  const { showToast } = useToast();
  const [rollbackStatements, { isLoading }] = useRollbackMutation();
  const [pending, setPending] = useState<RollbackRequest | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  // Built once per link, so the step components keep their identity across renders
  const steps = useMemo(() => buildRollbackSteps(invite, token), [invite, token]);

  // The wizard's final button only opens the confirm window. Nothing is sent until the user confirms there.
  const handleComplete = (values: Record<string, any>) => {
    const check = values?.rollback_check as RollbackCheckValue;
    if (!check || !canRollBack(check)) return;
    setDialogError(null);
    setPending({ invite: token, statement_ids: check.data.affected.map((row) => row.id) });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      const result = await rollbackStatements(pending).unwrap();
      const rolledBack = result.statements_rolled_back;
      showToast('success', `${rolledBack} ${plural(rolledBack)} rolled back`);
      setPending(null);
      setIsOpen(false);
      onRolledBack?.();
    } catch (error) {
      setDialogError(getErrorMessage(error));
    }
  };

  return (
    <>
      <Modal
        size="xl"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        header="Roll Back Statements"
        headerClassName="header"
        className="statements-wizard-modal statements-rollback-modal"
        includeFooter={false}
        unmountOnClose
      >
        <WizardForm steps={steps} onComplete={handleComplete} submitCaption="Roll Back Statements" />
      </Modal>
      <DestructiveConfirmDialog
        isOpen={pending !== null}
        title="Roll Back Statements"
        details={
          pending
            ? [
                { label: 'Company', value: invite.company.name },
                { label: 'Statement Month', value: dayjs(`${invite.month}-01`).format(monthDisplayFormat) },
                {
                  label: 'Clients',
                  value: invite.clients.length === 0 ? 'All clients' : String(invite.clients.length),
                },
                { label: 'Statements', value: String(pending.statement_ids.length) },
              ]
            : undefined
        }
        warnings={[]}
        acknowledgement={ROLLBACK_ACKNOWLEDGEMENTS}
        confirmPhrase="ROLL BACK"
        confirmLabel="Roll Back Statements"
        submittingLabel="Rolling back…"
        error={dialogError}
        isSubmitting={isLoading}
        dataCy="statements-rollback-confirm-dialog"
        onClose={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default StatementsRollbackWizard;
